import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { dashboardService } from '../../services/api';
import { DashboardStats, RecentActivity } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const StatCard = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const WelcomeMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const WelcomeTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const WelcomeText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
`;

const LoadingCard = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GrowthIndicator = styled.span<{ $growth: number }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ $growth }) => $growth >= 0 ? '#10b981' : '#ef4444'};
  margin-left: 0.5rem;
`;

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Só carregar dados se estiver autenticado
    if (isAuthenticated) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadDashboardData = async () => {
    // Verificação adicional de segurança
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [statsResponse, activityResponse] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getRecentActivity(5)
      ]);
      
      setStats(statsResponse.data);
      setRecentActivity(activityResponse.data);
    } catch (error: any) {
      // Não mostrar erro se for 401 (usuário não autenticado)
      if (error?.response?.status !== 401) {
        console.error('Erro ao carregar dados do dashboard:', error);
        showNotification('Erro ao carregar dados do dashboard', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Se não estiver autenticado, não renderizar nada
  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <>
        <WelcomeMessage>
          <WelcomeTitle>Bem-vindo ao Painel Administrativo!</WelcomeTitle>
          <WelcomeText>
            Carregando dados do sistema...
          </WelcomeText>
        </WelcomeMessage>

        <DashboardGrid>
          {[1, 2, 3, 4].map(i => (
            <LoadingCard key={i}>
              Carregando...
            </LoadingCard>
          ))}
        </DashboardGrid>
      </>
    );
  }

  if (!stats) {
    return (
      <WelcomeMessage>
        <WelcomeTitle>Erro ao carregar dados</WelcomeTitle>
        <WelcomeText>
          Não foi possível carregar as estatísticas do sistema.
        </WelcomeText>
      </WelcomeMessage>
    );
  }

  return (
    <>
      <WelcomeMessage>
        <WelcomeTitle>Bem-vindo ao Painel Administrativo!</WelcomeTitle>
        <WelcomeText>
          Sistema de imobiliária funcionando corretamente. Última atualização: {new Date(stats.updated_at).toLocaleString('pt-BR')}
        </WelcomeText>
      </WelcomeMessage>

      <DashboardGrid>
        <StatCard>
          <StatNumber>{formatNumber(stats.properties.total)}</StatNumber>
          <StatLabel>
            Total de Propriedades
            <GrowthIndicator $growth={stats.properties.growth_percentage}>
              {stats.properties.growth_percentage >= 0 ? '+' : ''}{stats.properties.growth_percentage}% este mês
            </GrowthIndicator>
          </StatLabel>
        </StatCard>
        
        <StatCard>
          <StatNumber>{formatNumber(stats.properties.active)}</StatNumber>
          <StatLabel>Propriedades Disponíveis</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatNumber>{formatNumber(stats.leads.total)}</StatNumber>
          <StatLabel>Total de Clientes</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatNumber>{formatNumber(stats.leads.recent)}</StatNumber>
          <StatLabel>Clientes Recentes (30 dias)</StatLabel>
        </StatCard>

        <StatCard>
          <StatNumber>{formatCurrency(stats.financial.avg_property_price)}</StatNumber>
          <StatLabel>Preço Médio dos Imóveis</StatLabel>
        </StatCard>

        <StatCard>
          <StatNumber>{formatCurrency(stats.financial.total_portfolio_value)}</StatNumber>
          <StatLabel>Valor Total do Portfólio</StatLabel>
        </StatCard>

        <StatCard>
          <StatNumber>{formatNumber(stats.properties.for_sale)}</StatNumber>
          <StatLabel>Para Venda</StatLabel>
        </StatCard>

        <StatCard>
          <StatNumber>{formatNumber(stats.properties.for_rent)}</StatNumber>
          <StatLabel>Para Locação</StatLabel>
        </StatCard>

        {stats.canal_pro.total_imports > 0 && (
          <>
            <StatCard>
              <StatNumber>{formatNumber(stats.canal_pro.total_imports)}</StatNumber>
              <StatLabel>Importações CanalPro</StatLabel>
            </StatCard>

            <StatCard>
              <StatNumber>{stats.canal_pro.success_rate}%</StatNumber>
              <StatLabel>Taxa de Sucesso Importações</StatLabel>
            </StatCard>
          </>
        )}

        <StatCard>
          <StatNumber>{formatNumber(stats.leads.new)}</StatNumber>
          <StatLabel>Novos Leads</StatLabel>
        </StatCard>

        <StatCard>
          <StatNumber>{formatNumber(stats.leads.converted)}</StatNumber>
          <StatLabel>Leads Convertidos</StatLabel>
        </StatCard>
      </DashboardGrid>
    </>
  );
};

export default AdminDashboard; 
